import type { Object3D } from 'three';
import { isAbstractSketchEdgeObject } from '../../../mesh/guards/isAbstractSketchEdgeObject';
import type { SketchGroup } from '../../../mesh/sketch/SketchGroup';
import type { ISketchObject } from '../../../mesh/sketch/ISketchObject';
import { SketchArcObject } from '../../../mesh/sketch/SketchArcObject';
import { SketchCircleObject } from '../../../mesh/sketch/SketchCircleObject';
import { SketchEllipseObject } from '../../../mesh/sketch/SketchEllipseObject';
import { SketchLineObject } from '../../../mesh/sketch/SketchLineObject';
import { SketchParabolaObject } from '../../../mesh/sketch/SketchParabolaObject';
import type { SketchPointObject } from '../../../mesh/sketch/SketchPointObject';
import {SketchBezierCurveClosureKind} from '../../../mesh/sketch/SketchSplineObject';
import {
	resolveExternalGeometrySourceRefEntityId,
	type ExternalGeometrySourceDescriptor,
} from '../model/ExternalGeometrySourceDescriptor';
import type { ProjectionResult } from '../model/ProjectionResult';
import type { CreateGeometryReferencesOptions } from '../model/SketchEntityReferenceTypes';
import type { TargetAnchorBindings } from '../model/TargetAnchorBindings';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import { ArcReferenceBuilder } from './builders/ArcReferenceBuilder';
import { CircleReferenceBuilder } from './builders/CircleReferenceBuilder';
import type { EllipseReferenceAxisMarkers } from './builders/EllipseReferenceBuilder';
import { EllipseReferenceBuilder } from './builders/EllipseReferenceBuilder';
import { EllipseArcReferenceBuilder } from './builders/EllipseArcReferenceBuilder';
import { LineReferenceBuilder } from './builders/LineReferenceBuilder';
import { ParabolaReferenceBuilder } from './builders/ParabolaReferenceBuilder';
import { SplineReferenceBuilder } from './builders/SplineReferenceBuilder';
import { StyleSplineReferenceBuilder } from './builders/StyleSplineReferenceBuilder';
import { SketchEntityReferenceObjectCleanupService } from './SketchEntityReferenceObjectCleanupService';
import { SketchReferenceTargetAnchorBindingCollector } from '../relation/SketchReferenceTargetAnchorBindingCollector';
import { SketchReferenceTargetEndpointCanonicalizer } from './SketchReferenceTargetEndpointCanonicalizer';
import type {
	SketchReferenceEntityFactoryInput,
	SketchReferenceEntityFactoryResult,
	SketchReferenceEntityFactoryDependencies,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceEntityFactory';

export type {
	SketchReferenceEntityFactoryInput,
	SketchReferenceEntityFactoryResult,
	SketchReferenceEntityFactoryDependencies,
} from '../../../../types/reference/sketch-entity-reference/SketchReferenceEntityFactory';

/**
 * 草图引用实体工厂。
 * 说明：
 * 1. `ProjectionResult` 是当前阶段唯一的创建分发入口；
 * 2. factory 负责“创建目标实体 + 补挂正式辅助点 + 产出同批次锚点拓扑映射”；
 * 3. 关系恢复、引用仓储等真正带副作用的后续阶段能力不在此类中实现。
 */
export class SketchReferenceEntityFactory {
	private readonly lineReferenceBuilder: LineReferenceBuilder;
	private readonly circleReferenceBuilder: CircleReferenceBuilder;
	private readonly arcReferenceBuilder: ArcReferenceBuilder;
	private readonly ellipseReferenceBuilder: EllipseReferenceBuilder;
	private readonly ellipseArcReferenceBuilder: EllipseArcReferenceBuilder;
	private readonly parabolaReferenceBuilder: ParabolaReferenceBuilder;
	private readonly splineReferenceBuilder: SplineReferenceBuilder;
	private readonly styleSplineReferenceBuilder: StyleSplineReferenceBuilder;
	private readonly objectCleanupService: SketchEntityReferenceObjectCleanupService;
	private readonly targetAnchorBindingCollector = new SketchReferenceTargetAnchorBindingCollector();
	private readonly targetEndpointCanonicalizer = new SketchReferenceTargetEndpointCanonicalizer();

	/**
	 * @param dependencies factory 依赖。
	 */
	public constructor(
		dependencies: SketchReferenceEntityFactoryDependencies = {},
	) {
		this.lineReferenceBuilder = dependencies.lineReferenceBuilder ?? new LineReferenceBuilder();
		this.circleReferenceBuilder = dependencies.circleReferenceBuilder ?? new CircleReferenceBuilder();
		this.arcReferenceBuilder = dependencies.arcReferenceBuilder ?? new ArcReferenceBuilder();
		this.ellipseReferenceBuilder = dependencies.ellipseReferenceBuilder ?? new EllipseReferenceBuilder();
		this.ellipseArcReferenceBuilder = dependencies.ellipseArcReferenceBuilder
			?? new EllipseArcReferenceBuilder();
		this.parabolaReferenceBuilder = dependencies.parabolaReferenceBuilder
			?? new ParabolaReferenceBuilder();
		this.splineReferenceBuilder = dependencies.splineReferenceBuilder ?? new SplineReferenceBuilder();
		this.styleSplineReferenceBuilder = dependencies.styleSplineReferenceBuilder
			?? new StyleSplineReferenceBuilder();
		this.objectCleanupService = dependencies.objectCleanupService
			?? new SketchEntityReferenceObjectCleanupService();
	}

	/**
	 * 批量创建草图引用实体。
	 *
	 * @param input 批量创建输入。
	 * @returns 创建结果。
	 */
	public createBatch(
		input: SketchReferenceEntityFactoryInput,
	): SketchReferenceEntityFactoryResult {
		const createdItems: SketchReferenceEntityFactoryResult['createdItems'] = [];
		const createdEntities: Array<Object3D & ISketchObject> = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		const preferredTargetAnchorBindings = new Map(
			[...(input.preferredTargetAnchorBindings ?? new Map()).entries()]
				.map(([anchorId, points]) => [anchorId, [...points]]),
		);

		input.projectionResults.forEach((projectionResult) => {
			let entity: (Object3D & ISketchObject) | null = null;
			try {
				entity = this.buildEntityFromProjectionResult(
					input.targetSketch,
					projectionResult,
					preferredTargetAnchorBindings,
				);
				this.applyCreationOptions(entity, input.options);
				input.targetSketch.add(entity);
				this.attachAuxiliarySketchObjects(input.targetSketch, entity);
				createdItems.push({
					projectionResult,
					targetEntity: entity,
				});
				createdEntities.push(entity);
				this.mergeTargetAnchorBindings(
					preferredTargetAnchorBindings,
					this.targetAnchorBindingCollector.collect({
						items: [{projectionResult, targetEntity: entity}],
						resolveSourceObject: input.resolveSourceObject,
					}),
				);
			} catch (error) {
				if (entity) {
					this.cleanupFailedEntity(entity);
				}
				diagnostics.push(createExecutionDiagnostic({
					kind: 'projection-invalid',
					level: 'error',
					message: `基于投影结果创建目标实体失败: ${error instanceof Error ? error.message : String(error)}`,
					sourceRefEntityId: this.resolveSourceRefEntityId(projectionResult),
				}));
			}
		});

		const targetAnchorBindings = this.targetAnchorBindingCollector.collect({
			items: createdItems,
			resolveSourceObject: input.resolveSourceObject,
		});
		this.targetEndpointCanonicalizer.canonicalize(input.targetSketch, targetAnchorBindings);

		return {
			createdItems,
			createdEntities,
			targetAnchorBindings,
			diagnostics,
		};
	}


	/**
	 * 应用目标实体创建选项。
	 *
	 * 说明：
	 * 1. SolidWorks 的“作为构造线”只改变转换后目标草图实体的输出属性；
	 * 2. 这里集中处理，避免每个 ProjectionResult builder 都重复接收同一个开关；
	 * 3. 只对正式草图边对象写入构造几何状态，防止误改控制点或非草图对象。
	 *
	 * @param targetEntity 新建目标实体。
	 * @param options 创建选项。
	 */
	private applyCreationOptions(
		targetEntity: Object3D,
		options: Pick<CreateGeometryReferencesOptions, 'asConstruction'> | undefined,
	): void {
		if (!options?.asConstruction || !isAbstractSketchEdgeObject(targetEntity)) {
			return;
		}

		targetEntity.isConstruction = true;
	}

	/**
	 * 根据投影结果构建单个目标实体。
	 *
	 * @param targetSketch 目标草图。
	 * @param projectionResult 投影结果。
	 * @returns 已创建的目标实体。
	 */
	private buildEntityFromProjectionResult(
		targetSketch: SketchGroup,
		projectionResult: ProjectionResult,
		preferredTargetAnchorBindings: TargetAnchorBindings,
	): Object3D & ISketchObject {
		switch (projectionResult.projectionResultKind) {
			case 'line':
				return this.lineReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'circle':
				return this.circleReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'arc':
				return this.arcReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'ellipse':
				return this.ellipseReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'ellipse-arc':
				return this.ellipseArcReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'parabola':
				return this.parabolaReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
				});
			case 'spline':
				{
					const allowsEndpointReuse = projectionResult.geometryPayload.curveMetadata.closureKind
						!== SketchBezierCurveClosureKind.Closed;
					// 闭合周期样条的内部闭合点不是 topology 开放端点，禁止在对象创建前复用同批 keeper。
					const startPoint = allowsEndpointReuse
						? this.resolvePreferredEndpoint(projectionResult, 'start', preferredTargetAnchorBindings)
						: undefined;
					const endPoint = allowsEndpointReuse
						? this.resolvePreferredEndpoint(projectionResult, 'end', preferredTargetAnchorBindings)
						: undefined;
				return this.splineReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
					...(startPoint ? {startPoint} : {}),
					...(endPoint ? {endPoint} : {}),
				});
				}
			case 'style-spline':
				{
					const startPoint = this.resolvePreferredEndpoint(projectionResult, 'start', preferredTargetAnchorBindings);
					const endPoint = this.resolvePreferredEndpoint(projectionResult, 'end', preferredTargetAnchorBindings);
				return this.styleSplineReferenceBuilder.build({
					targetSketch,
					geometryPayload: projectionResult.geometryPayload,
					...(startPoint ? {startPoint} : {}),
					...(endPoint ? {endPoint} : {}),
				});
				}
		}
	}

	/**
	 * 解析 replacement 构造时可直接复用的目标端点。
	 *
	 * @param projectionResult 当前投影结果。
	 * @param endpoint 起点或终点。
	 * @param preferredTargetAnchorBindings 本批已建立的锚点绑定。
	 * @returns 命中时返回 keeper 点，否则返回 undefined。
	 */
	private resolvePreferredEndpoint(
		projectionResult: ProjectionResult,
		endpoint: 'start' | 'end',
		preferredTargetAnchorBindings: TargetAnchorBindings,
	): SketchPointObject | undefined {
		const binding = projectionResult.endpointAnchorBindings?.find(candidate => (
			candidate.targetEndpoint === endpoint
		));
		return binding
			? preferredTargetAnchorBindings.get(binding.sourceAnchorId)?.[0]
			: undefined;
	}

	/** 合并本批新建目标提供的锚点绑定。 */
	private mergeTargetAnchorBindings(
		target: TargetAnchorBindings,
		source: TargetAnchorBindings,
	): void {
		source.forEach((points, anchorId) => {
			const existingPoints = target.get(anchorId) ?? [];
			target.set(anchorId, [
				...new Map([...existingPoints, ...points].map(point => [point.uuid, point])).values(),
			]);
		});
	}

	/**
	 * 将实体创建所需的辅助对象挂到目标草图。
	 * 说明：
	 * 1. 挂载策略优先对齐当前正式绘图链路，而不是 preview 的临时场景策略；
	 * 2. 只补挂当前对象体系没有作为子节点管理、但交互/后续关系恢复需要显式进入场景树的控制点；
	 * 3. 不在此处释放或替换任何共享材质资源。
	 *
	 * @param targetSketch 目标草图。
	 * @param targetEntity 新建目标实体。
	 */
	private attachAuxiliarySketchObjects(
		targetSketch: SketchGroup,
		targetEntity: Object3D,
	): void {
		if (targetEntity instanceof SketchLineObject) {
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.startPoint);
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.endPoint);
			return;
		}

		if (targetEntity instanceof SketchArcObject) {
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.getStartPoint());
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.getEndPoint());
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.getCenterPoint());
			return;
		}

		if (targetEntity instanceof SketchCircleObject) {
			this.attachAuxiliarySketchObject(targetSketch, targetEntity, targetEntity.getCenterControlPoint());
			return;
		}

		if (targetEntity instanceof SketchEllipseObject) {
			const ellipseAuxiliaryPoints = new Set<Object3D>([
				targetEntity.getCenterPoint(),
				targetEntity.getStartPoint(),
				targetEntity.getEndPoint(),
				...targetEntity.getQuadrantPoints(),
			]);
			ellipseAuxiliaryPoints.forEach((point) => {
				this.attachAuxiliarySketchObject(targetSketch, targetEntity, point);
			});
			return;
		}

		if (targetEntity instanceof SketchParabolaObject) {
			targetEntity.getControlPoints().forEach((point) => {
				this.attachAuxiliarySketchObject(targetSketch, targetEntity, point);
			});
			return;
		}
	}

	/**
	 * 按目标实体归属补挂辅助点。
	 * 说明：
	 * 1. 部分草图对象会把控制点作为自身子节点管理，例如圆弧端点与圆心；
	 * 2. 这些点不能再次 add 到 SketchGroup，否则 Three.js 会把它从目标实体子树移走，形成独立点；
	 * 3. 只有线段端点、椭圆控制点这类未被目标实体子树管理的点，才需要补挂到目标草图根下。
	 *
	 * @param targetSketch 目标草图。
	 * @param targetEntity 目标实体。
	 * @param auxiliaryObject 需要检查的辅助对象。
	 */
	private attachAuxiliarySketchObject(
		targetSketch: SketchGroup,
		targetEntity: Object3D,
		auxiliaryObject: Object3D,
	): void {
		if (
			auxiliaryObject === targetEntity
			|| this.isObjectInsideTree(targetEntity, auxiliaryObject)
			|| auxiliaryObject.parent === targetSketch
		) {
			return;
		}

		targetSketch.add(auxiliaryObject);
	}

	/**
	 * 判断对象是否已经位于指定根对象子树内。
	 *
	 * @param root 子树根对象。
	 * @param target 目标对象。
	 * @returns 目标对象位于根对象子树内时返回 true。
	 */
	private isObjectInsideTree(root: Object3D, target: Object3D): boolean {
		let current: Object3D | null = target;
		while (current) {
			if (current === root) {
				return true;
			}
			current = current.parent;
		}
		return false;
	}

	/**
	 * 回收创建失败后已经部分挂到场景树的对象。
	 * 说明：
	 * 1. builder / attach 流程可能在“主实体已 add，但附属点尚未完全挂完”时抛错；
	 * 2. 若不立刻清理，会把半成品对象留在目标草图里，污染后续选择与捕捉；
	 * 3. 这里默认只释放 geometry，不触碰共享材质。
	 *
	 * @param targetEntity 创建失败的目标实体。
	 */
	private cleanupFailedEntity(targetEntity: Object3D): void {
		this.objectCleanupService.cleanupTargetEntity(targetEntity);
	}

	/**
	 * 解析投影结果对应的来源实体标识。
	 *
	 * @param projectionResult 投影结果。
	 * @returns 规范化后的来源实体标识。
	 */
	private resolveSourceRefEntityId(projectionResult: ProjectionResult): string {
		return resolveExternalGeometrySourceRefEntityId(projectionResult.sourceDescriptor);
	}
}

/**
 * 读取附加在椭圆对象上的引用轴端标记。
 *
 * @param ellipse 椭圆对象。
 * @returns 轴端标记点集合；不存在时返回 null。
 */
export function getEllipseReferenceAxisMarkers(
	ellipse: SketchEllipseObject,
): EllipseReferenceAxisMarkers | null {
	const markers = Reflect.get(ellipse, '__referenceAxisMarkers');
	return markers ? markers as EllipseReferenceAxisMarkers : null;
}
